import React, {useState, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../text';
import {hooks} from '../hooks';
import {utils} from '../utils';
import {theme} from '../constants';
import {components} from '../components';
import axiosInstance from '../services/axiosInstance';
import { listCache } from '../utils/listCache';
import {formatDateTime} from '../utils/formatText';
import { useLanguage } from '../context/LanguageContext';
import { compressImage } from '../utils/imageUtils';
import { MdBusiness, MdLocationOn, MdDateRange, MdTitle, MdPersonAdd, MdNoteAdd, MdClose, MdDelete, MdAttachFile, MdPerson } from 'react-icons/md';

const BusinessIcon = MdBusiness as any;
const LocationIcon = MdLocationOn as any;
const DateRangeIcon = MdDateRange as any;
const TitleIcon = MdTitle as any;
const PersonAddIcon = MdPersonAdd as any;
const PersonIcon = MdPerson as any;
const NoteAddIcon = MdNoteAdd as any;
const CloseIcon = MdClose as any;
const DeleteIcon = MdDelete as any;
const AttachFileIcon = MdAttachFile as any;

export const DetailMarketingActivity: React.FC = () => {
  const {state, search} = useLocation();
  const navigate = hooks.useAppNavigate();
  const searchParams = new URLSearchParams(search);
  const activityIdFromUrl = searchParams.get('id');
  const { t } = useLanguage();
  const { authSlice } = hooks.useAppSelector((s: any) => s);

  const [activity, setActivity] = useState<any>(state?.activity || null);
  const [loading, setLoading] = useState(!state?.activity);

  const canDelete = !!activity && (
    String(authSlice.userId) === String(activity.user_id) ||
    authSlice.permissions?.includes('activity:view_all')
  );

  const [participants, setParticipants] = useState<any[]>([]);
  const [minutes, setMinutes] = useState<any[]>([]);

  // Add participant form
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [participantPosition, setParticipantPosition] = useState('');

  // Add minutes form
  const [showAddMinute, setShowAddMinute] = useState(false);
  const [minuteIssue, setMinuteIssue] = useState('');
  const [minuteDate, setMinuteDate] = useState('');
  const [minuteAction, setMinuteAction] = useState('');
  const [minuteDepartment, setMinuteDepartment] = useState('');
  const [minutePic, setMinutePic] = useState('');
  const [minuteTarget, setMinuteTarget] = useState('');
  const [minuteStatus, setMinuteStatus] = useState('');

  // Documentation form
  const [documentations, setDocumentations] = useState<any[]>([]);
  const [showAddDocumentation, setShowAddDocumentation] = useState(false);
  const [documentationFiles, setDocumentationFiles] = useState<File[]>([]);
  const docFileInputRef = useRef<HTMLInputElement>(null);
  const [previewDocUrl, setPreviewDocUrl] = useState<string | null>(null);

  // Delete Confirmation Modal State
  const [deleteConfig, setDeleteConfig] = useState<{
    isOpen: boolean;
    type: 'activity' | 'participant' | 'minute' | 'documentation' | null;
    idToDelete: number | null;
  }>({ isOpen: false, type: null, idToDelete: null });

  useEffect(() => {
    const fetchActivity = async () => {
      const id = state?.activity?.id || activityIdFromUrl;
      if (!id) return;

      try {
        setLoading(true);
        const response = await axiosInstance.get(`/marketing-activities/${id}`);
        setActivity(response.data);
        if (response.data.participants) setParticipants(response.data.participants);
        if (response.data.minutes) setMinutes(response.data.minutes);
        if (response.data.documentations) setDocumentations(response.data.documentations);
      } catch (error) {
        console.error('Failed to fetch activity details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [state?.activity?.id, activityIdFromUrl]);

  const handleAddParticipant = async () => {
    if (!participantName) return;
    try {
      const response = await axiosInstance.post(`/marketing-activities/${activity.id}/participants`, {
        name: participantName,
        position: participantPosition
      });
      setParticipants([...participants, response.data]);
      setParticipantName('');
      setParticipantPosition('');
      setShowAddParticipant(false);
    } catch (error) {
      console.error('Failed to add participant:', error);
    }
  };


  const handleAddMinute = async () => {
    if (!minuteIssue) return;
    try {
      const response = await axiosInstance.post(`/marketing-activities/${activity.id}/minutes`, {
        issue: minuteIssue,
        date: minuteDate || null,
        action: minuteAction,
        department: minuteDepartment,
        pic: minutePic,
        target: minuteTarget,
        status: minuteStatus
      });
      setMinutes([...minutes, response.data]);
      setMinuteIssue('');
      setMinuteDate('');
      setMinuteAction('');
      setMinuteDepartment('');
      setMinutePic('');
      setMinuteTarget('');
      setMinuteStatus('');
      setShowAddMinute(false);
    } catch (error) {
      console.error('Failed to add minute:', error);
    }
  };


  const handleDocFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const processedFiles = await Promise.all(newFiles.map(async (file) => {
        if (file.type.startsWith('image/')) {
          try { return await compressImage(file); } catch (err) { return file; }
        }
        return file;
      }));
      setDocumentationFiles(prev => [...prev, ...processedFiles]);
    }
    if (docFileInputRef.current) docFileInputRef.current.value = '';
  };

  const handleAddDocumentation = async () => {
    if (documentationFiles.length === 0) return;
    try {
      const formData = new FormData();
      documentationFiles.forEach(file => formData.append('documents', file));
      const response = await axiosInstance.post(`/marketing-activities/${activity.id}/documentations`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDocumentations([...documentations, ...response.data]);
      setDocumentationFiles([]);
      setShowAddDocumentation(false);
    } catch (error) {
      console.error('Failed to add documentation:', error);
    }
  };

  const executeDelete = async () => {
    const { type, idToDelete } = deleteConfig;
    if (!activity?.id) return;

    try {
      if (type === 'activity') {
        await axiosInstance.delete(`/marketing-activities/${activity.id}`);
        const cache = listCache.restore('marketing-activities');
        if (cache) {
          listCache.save('marketing-activities', {
            ...cache,
            items: cache.items.filter((item: any) => item.id !== activity.id),
          });
        }
        setDeleteConfig({ isOpen: false, type: null, idToDelete: null });
        navigate(-1);
        return;
      }
      if (!idToDelete) return;
      if (type === 'participant') {
        await axiosInstance.delete(`/marketing-activities/${activity.id}/participants/${idToDelete}`);
        setParticipants(prev => prev.filter(p => p.id !== idToDelete));
      } else if (type === 'minute') {
        await axiosInstance.delete(`/marketing-activities/${activity.id}/minutes/${idToDelete}`);
        setMinutes(prev => prev.filter(m => m.id !== idToDelete));
      } else if (type === 'documentation') {
        await axiosInstance.delete(`/marketing-activities/${activity.id}/documentations/${idToDelete}`);
        setDocumentations(prev => prev.filter(d => d.id !== idToDelete));
      }
    } catch (error) {
      console.error(`Failed to delete ${type}:`, error);
    } finally {
      setDeleteConfig({ isOpen: false, type: null, idToDelete: null });
    }
  };

  // Signature modal states & refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signingParticipantId, setSigningParticipantId] = useState<number | null>(null);
  
  // State untuk Preview Tanda Tangan
  const [previewSignUrl, setPreviewSignUrl] = useState<string | null>(null);
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';

  const startDrawing = (e: any) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: any) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSaveSignature = async () => {
    if (!canvasRef.current || signingParticipantId === null) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    
    try {
      // Ubah dataURL base64 menjadi Blob/File fisik
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `signature_${signingParticipantId}.png`, { type: 'image/png' });

      // Gunakan FormData persis seperti halaman form Task/Attendance
      const formData = new FormData();
      formData.append('sign', file);

      // Tangkap respons dari backend yang sudah berisi path gambar (cth: uploads/...)
      const response = await axiosInstance.put(`/marketing-activities/${activity.id}/participants/${signingParticipantId}/sign`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setParticipants(participants.map(p => p.id === signingParticipantId ? { ...p, sign: response.data.sign } : p));
      setShowSignatureModal(false);
      setSigningParticipantId(null);
    } catch (error) {
      console.error('Failed to save signature:', error);
    }
  };

  const renderHeader = () => (
    <components.Header
      title={t.marketingActivity?.detailTitle || "Activity Details"}
      goBack={true}
      trash={canDelete}
      onTrash={() => setDeleteConfig({ isOpen: true, type: 'activity', idToDelete: activity?.id })}
    />
  );

  const renderContent = () => {
    if (loading) {
      return (
        <main className="container" style={{ marginTop: 60, paddingBottom: 120 }}>
          <text.T16 style={{textAlign: 'center', marginTop: 40}}>{t.marketingActivity?.loading || "Loading details..."}</text.T16>
        </main>
      );
    }

    if (!activity) {
      return (
        <main className="container" style={{ marginTop: 60, paddingBottom: 120 }}>
          <text.T16 style={{textAlign: 'center', marginTop: 40}}>{t.marketingActivity?.notFound || "Activity not found."}</text.T16>
        </main>
      );
    }

    return (
      <main className="container" style={{ marginTop: 60, paddingBottom: 120 }}>
        <div style={{ backgroundColor: 'white', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 24 }}>
          <div style={{ padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 'bold', backgroundColor: '#EFF6FF', color: '#3B82F6', display: 'inline-block', marginBottom: 12 }}>
            {activity.activity_type}
          </div>

          <text.H4 style={{marginBottom: 16}}>{activity.subject ? activity.subject.charAt(0).toUpperCase() + activity.subject.slice(1) : ''}</text.H4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2 }}><DateRangeIcon size={20} /></div>
              <div>
                <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.marketingActivity?.dateAndTime || 'Date & Time'}</text.T12>
                <text.T14 style={{ fontWeight: '500' }}>{formatDateTime(activity.datetime)}</text.T14>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2 }}><BusinessIcon size={20} /></div>
              <div>
                <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.marketingActivity?.companyName || 'Company Name'}</text.T12>
                <text.T14 style={{ fontWeight: '500' }}>{activity.company_name}</text.T14>
              </div>
            </div>
            {activity.user_name && (
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ color: '#9CA3AF', marginTop: 2 }}><PersonIcon size={20} /></div>
                <div>
                  <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.common?.createdBy || 'Created By'}</text.T12>
                  <text.T14 style={{ fontWeight: '500', textTransform: 'capitalize' }}>{activity.user_name}</text.T14>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ color: '#9CA3AF', marginTop: 2 }}><LocationIcon size={20} /></div>
              <div>
                <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.common?.location || 'Location'}</text.T12>
                <text.T14 style={{ fontWeight: '500' }}>{activity.address || t.marketingActivity?.noAddress || 'No address provided'}</text.T14>
                {activity.coordinates && <text.T12 style={{ color: '#9CA3AF' }}>{activity.coordinates}</text.T12>}
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
            <text.T12 style={{ color: '#6B7280', marginBottom: 4 }}>{t.common?.description || 'Description'}</text.T12>
            <text.T14 style={{ lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{activity.description ? activity.description.charAt(0).toUpperCase() + activity.description.slice(1) : '-'}</text.T14>
          </div>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <text.H5>{t.marketingActivity?.participants || 'Participants'}</text.H5>
            <div onClick={() => setShowAddParticipant(!showAddParticipant)} style={{ display: 'flex', alignItems: 'center', gap: 4, color: theme.colors.mainColor, cursor: 'pointer' }}><PersonAddIcon size={18} /><text.T14 style={{ fontWeight: '600' }}>{t.marketingActivity?.add || 'Add'}</text.T14></div>
          </div>
          {showAddParticipant && (
            <div style={{ padding: '16px 0', borderTop: '1px dashed #E5E7EB', marginBottom: 16 }}>
              <input type="text" placeholder={t.marketingActivity?.participantName || "Participant Name"} value={participantName} onChange={(e) => setParticipantName(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 10, outline: 'none', boxSizing: 'border-box' }} />
              <input type="text" placeholder={t.marketingActivity?.position || "Position"} value={participantPosition} onChange={(e) => setParticipantPosition(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 10, outline: 'none', boxSizing: 'border-box' }} />
              <div style={{ display: 'flex', gap: 10 }}><components.Button title={t.common?.cancel || "Cancel"} style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }} containerStyle={{ flex: 1 }} onClick={() => setShowAddParticipant(false)} /><components.Button title={t.common?.save || "Save"} containerStyle={{ flex: 1 }} onClick={handleAddParticipant} /></div>
            </div>
          )}
          {participants.length === 0 ? (<text.T14 style={{ color: '#9CA3AF' }}>{t.marketingActivity?.noParticipants || 'No participants added yet.'}</text.T14>) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {participants.map((p, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', backgroundColor: '#F9FAFB', borderRadius: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <text.T14 style={{ fontWeight: 'bold', color: '#4B5563' }}>{p.name?.charAt(0)}</text.T14>
                    </div>
                    <div>
                      <text.T14 style={{ fontWeight: '600', color: theme.colors.mainDark }}>{p.name ? p.name.charAt(0).toUpperCase() + p.name.slice(1) : ''}</text.T14>
                      {p.position && <text.T12 style={{ color: '#6B7280' }}>{p.position.charAt(0).toUpperCase() + p.position.slice(1)}</text.T12>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {p.sign ? (
                      <div 
                        onClick={() => setPreviewSignUrl(p.sign)}
                        style={{ padding: '2px 8px', border: `1px solid ${theme.colors.mainColor}`, backgroundColor: '#EFF6FF', borderRadius: 6, cursor: 'pointer' }}
                      >
                        <text.T14 style={{ color: theme.colors.mainColor, fontWeight: 'bold', fontSize: 12 }}>{t.marketingActivity?.signed || 'Signed'}</text.T14>
                      </div>
                    ) : (
                      <div 
                        onClick={() => { setSigningParticipantId(p.id); setShowSignatureModal(true); }}
                        style={{ padding: '4px 10px', backgroundColor: theme.colors.mainColor, color: 'white', borderRadius: 6, cursor: 'pointer' }}
                      >
                        <text.T14 style={{ fontWeight: '600', color: 'white', fontSize: 12 }}>{t.marketingActivity?.sign || 'Sign'}</text.T14>
                      </div>
                    )}
                    <div onClick={() => setDeleteConfig({ isOpen: true, type: 'participant', idToDelete: p.id })} style={{ cursor: 'pointer', padding: 4 }}>
                      <DeleteIcon size={20} color="#EF4444" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <text.H5>{t.marketingActivity?.minutesTitle || 'Meeting Minutes'}</text.H5>
            <div onClick={() => setShowAddMinute(!showAddMinute)} style={{ display: 'flex', alignItems: 'center', gap: 4, color: theme.colors.mainColor, cursor: 'pointer' }}><NoteAddIcon size={18} /><text.T14 style={{ fontWeight: '600' }}>{t.marketingActivity?.add || 'Add'}</text.T14></div>
          </div>
          {showAddMinute && (
            <div style={{ padding: '16px 0', borderTop: '1px dashed #E5E7EB', marginBottom: 16 }}>
              <textarea placeholder={t.marketingActivity?.issue || "Issue *"} value={minuteIssue} onChange={(e) => setMinuteIssue(e.target.value)} style={{ width: '100%', minHeight: 60, padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 10, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
                <div style={{ width: '100%' }}>
                  <text.T12 style={{ color: '#6B7280', display: 'block', marginBottom: 4 }}>{t.marketingActivity?.date || 'Date'}</text.T12>
                  <input type="date" value={minuteDate} onChange={(e) => setMinuteDate(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ width: '100%' }}>
                  <text.T12 style={{ color: '#6B7280', display: 'block', marginBottom: 4 }}>{t.marketingActivity?.status || 'Status'}</text.T12>
                  <select value={minuteStatus} onChange={(e) => setMinuteStatus(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', outline: 'none', backgroundColor: 'white', boxSizing: 'border-box' }}>
                    <option value="">{t.marketingActivity?.selectStatus || 'Select Status'}</option>
                    <option value="Open">Open</option>
                    <option value="Continued">Continued</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>

              <textarea placeholder={t.marketingActivity?.action || "Action"} value={minuteAction} onChange={(e) => setMinuteAction(e.target.value)} style={{ width: '100%', minHeight: 60, padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 10, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              <input type="text" placeholder={t.marketingActivity?.department || "Department"} value={minuteDepartment} onChange={(e) => setMinuteDepartment(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 10, outline: 'none', boxSizing: 'border-box' }} />
              <input type="text" placeholder={t.marketingActivity?.pic || "PIC"} value={minutePic} onChange={(e) => setMinutePic(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 10, outline: 'none', boxSizing: 'border-box' }} />
              <input type="text" placeholder={t.marketingActivity?.target || "Target"} value={minuteTarget} onChange={(e) => setMinuteTarget(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #E5E7EB', marginBottom: 10, outline: 'none', boxSizing: 'border-box' }} />
              
              <div style={{ display: 'flex', gap: 10 }}><components.Button title={t.common?.cancel || "Cancel"} style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }} containerStyle={{ flex: 1 }} onClick={() => setShowAddMinute(false)} /><components.Button title={t.common?.save || "Save"} containerStyle={{ flex: 1 }} onClick={handleAddMinute} /></div>
            </div>
          )}
          {minutes.length === 0 ? (<text.T14 style={{ color: '#9CA3AF' }}>{t.marketingActivity?.noMinutes || 'No minutes recorded yet.'}</text.T14>) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {minutes.map((m, idx) => (
                <div key={idx} style={{ padding: '12px 14px', backgroundColor: '#F9FAFB', borderRadius: 8, borderLeft: `3px solid ${theme.colors.mainColor}`, position: 'relative' }}>
                  <div onClick={() => setDeleteConfig({ isOpen: true, type: 'minute', idToDelete: m.id })} style={{ position: 'absolute', top: 10, right: 10, cursor: 'pointer', padding: 4, zIndex: 2, backgroundColor: 'rgba(249, 250, 251, 0.5)', borderRadius: '50%' }}>
                    <DeleteIcon size={20} color="#EF4444" />
                  </div>
                  <text.T14 style={{ fontWeight: 'bold', color: theme.colors.mainDark, display: 'block', marginBottom: 4 }}>{t.marketingActivity?.issue?.replace(' *', '') || 'Issue'}:</text.T14>
                  <text.T14 style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: theme.colors.mainDark, display: 'block', marginBottom: m.action ? 8 : 0 }}>{m.issue ? m.issue.charAt(0).toUpperCase() + m.issue.slice(1) : ''}</text.T14>
                  
                  {m.action && (
                    <>
                      <text.T14 style={{ fontWeight: 'bold', color: theme.colors.mainDark, display: 'block', marginBottom: 4, marginTop: 8 }}>{t.marketingActivity?.action || 'Action'}:</text.T14>
                      <text.T14 style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, color: theme.colors.mainDark, display: 'block' }}>{m.action ? m.action.charAt(0).toUpperCase() + m.action.slice(1) : ''}</text.T14>
                    </>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12, backgroundColor: '#FFFFFF', padding: 10, borderRadius: 8, border: '1px solid #E5E7EB' }}>
                    {m.date && <div><text.T12 style={{ color: '#9CA3AF' }}>{t.marketingActivity?.date || 'Date'}</text.T12><text.T14 style={{ display: 'block', fontWeight: '500' }}>{new Date(m.date).toLocaleDateString()}</text.T14></div>}
                    {m.status && <div><text.T12 style={{ color: '#9CA3AF' }}>{t.marketingActivity?.status || 'Status'}</text.T12><text.T14 style={{ display: 'block', fontWeight: '500' }}>{m.status}</text.T14></div>}
                    {m.department && <div><text.T12 style={{ color: '#9CA3AF' }}>Dept</text.T12><text.T14 style={{ display: 'block', fontWeight: '500' }}>{m.department.charAt(0).toUpperCase() + m.department.slice(1)}</text.T14></div>}
                    {m.target && <div><text.T12 style={{ color: '#9CA3AF' }}>{t.marketingActivity?.target || 'Target'}</text.T12><text.T14 style={{ display: 'block', fontWeight: '500' }}>{m.target.charAt(0).toUpperCase() + m.target.slice(1)}</text.T14></div>}
                    {m.pic && <div style={{ gridColumn: '1 / -1' }}><text.T12 style={{ color: '#9CA3AF' }}>{t.marketingActivity?.pic || 'PIC'}</text.T12><text.T14 style={{ display: 'block', fontWeight: '500' }}>{m.pic.charAt(0).toUpperCase() + m.pic.slice(1)}</text.T14></div>}
                  </div>

                  {m.created_at && <text.T12 style={{ color: '#9CA3AF', display: 'block', marginTop: 12, borderTop: '1px dashed #E5E7EB', paddingTop: 8 }}>{t.marketingActivity?.added || 'Added:'} {formatDateTime(m.created_at)}</text.T12>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documentation Section */}
        <div style={{ backgroundColor: 'white', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <text.H5>{t.marketingActivity?.documentation || 'Documentation'}</text.H5>
            <div onClick={() => setShowAddDocumentation(!showAddDocumentation)} style={{ display: 'flex', alignItems: 'center', gap: 4, color: theme.colors.mainColor, cursor: 'pointer' }}>
              <NoteAddIcon size={18} />
              <text.T14 style={{ fontWeight: '600' }}>{t.marketingActivity?.add || 'Add'}</text.T14>
            </div>
          </div>
          {showAddDocumentation && (
            <div style={{ padding: '16px 0', borderTop: '1px dashed #E5E7EB', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10, marginBottom: 10 }}>
                {documentationFiles.map((file, index) => (
                  <div key={index} style={{ position: 'relative', width: 80, height: 80, flexShrink: 0, borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                    {file.type.startsWith('image/') ? (
                      <img src={URL.createObjectURL(file)} alt="preview" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                    ) : (
                      <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB'}}><AttachFileIcon size={24} color={theme.colors.mainColor} /></div>
                    )}
                    <div onClick={() => setDocumentationFiles(prev => prev.filter((_, i) => i !== index))} style={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255,255,255,0.8)', borderRadius: '50%', padding: 4, cursor: 'pointer' }}><DeleteIcon size={16} color="#EF4444" /></div>
                  </div>
                ))}
                <div onClick={() => docFileInputRef.current?.click()} style={{ width: 80, height: 80, flexShrink: 0, border: '1px dashed #9CA3AF', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB', cursor: 'pointer' }}><AttachFileIcon size={24} color="#9CA3AF" /></div>
              </div>
              <input type="file" multiple ref={docFileInputRef} onChange={handleDocFileChange} style={{display: 'none'}} />
              <div style={{ display: 'flex', gap: 10 }}>
                <components.Button title={t.common?.cancel || "Cancel"} style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }} containerStyle={{ flex: 1 }} onClick={() => {setShowAddDocumentation(false); setDocumentationFiles([]);}} />
                <components.Button title={t.marketingActivity?.addDocumentation || "Add Documentation"} containerStyle={{ flex: 1 }} onClick={handleAddDocumentation} />
              </div>
            </div>
          )}
          {documentations.length === 0 ? (
            <text.T14 style={{ color: '#9CA3AF' }}>{t.marketingActivity?.noDocumentation || 'No documentation added yet.'}</text.T14>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {documentations.map((doc, idx) => {
                const fileUrl = `${imageBaseUrl}${doc.file_path.replace(/\\/g, '/')}`;
                const isImage = doc.file_path.match(/\.(jpg|jpeg|png|gif|webp)$/i);
                return (
                  <div key={idx} style={{ position: 'relative', width: 100, height: 100, borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                    {isImage ? (
                      <img src={fileUrl} alt="doc" style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }} onClick={() => setPreviewDocUrl(fileUrl)} />
                    ) : (
                      <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F9FAFB', textDecoration: 'none' }}><AttachFileIcon size={32} color={theme.colors.mainColor} /></a>
                    )}
                    <div onClick={() => setDeleteConfig({ isOpen: true, type: 'documentation', idToDelete: doc.id })} style={{ position: 'absolute', top: 4, right: 4, cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.8)', padding: 4, borderRadius: '50%', zIndex: 2 }}><DeleteIcon size={16} color="#EF4444" /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    );
  };

  const renderSignatureModal = () => {
    if (!showSignatureModal) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 400, boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <text.H5>{t.marketingActivity?.participantSignature || 'Participant Signature'}</text.H5>
            <div onClick={() => { setShowSignatureModal(false); setSigningParticipantId(null); }} style={{ cursor: 'pointer' }}>
              <CloseIcon size={24} color="#9CA3AF" />
            </div>
          </div>
          
          <div style={{ border: '1px dashed #9CA3AF', borderRadius: 8, overflow: 'hidden', marginBottom: 16, backgroundColor: '#F9FAFB' }}>
            <canvas
              ref={canvasRef}
              width={400}
              height={200}
              style={{ touchAction: 'none', display: 'block', width: '100%' }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={endDrawing}
              onMouseLeave={endDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={endDrawing}
            />
          </div>
          
          <div style={{ display: 'flex', gap: 12 }}>
            <components.Button title={t.marketingActivity?.clear || "Clear"} style={{ backgroundColor: '#F3F4F6', color: '#4B5563', padding: '10px' }} containerStyle={{ flex: 1 }} onClick={clearCanvas} />
            <components.Button title={t.marketingActivity?.saveSignature || "Save Signature"} style={{ padding: '10px' }} containerStyle={{ flex: 2 }} onClick={handleSaveSignature} />
          </div>
        </div>
      </div>
    );
  };

  const renderSignaturePreviewModal = () => {
    if (!previewSignUrl) return null;
    
    // Cek apakah string URL berupa base64/http lengkap, atau merupakan relative path dari database
    const isBase64OrHttp = previewSignUrl.startsWith('data:') || previewSignUrl.startsWith('http');
    const finalUrl = isBase64OrHttp ? previewSignUrl : `${imageBaseUrl}${previewSignUrl}`;

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }} onClick={() => setPreviewSignUrl(null)}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 400, boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <text.H5>{t.marketingActivity?.signaturePreview || 'Signature Preview'}</text.H5>
            <div onClick={() => setPreviewSignUrl(null)} style={{ cursor: 'pointer' }}>
              <CloseIcon size={24} color="#9CA3AF" />
            </div>
          </div>
          <div style={{ width: '100%', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, display: 'flex', justifyContent: 'center' }}>
            <img src={finalUrl} alt="Signature" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
          </div>
        </div>
      </div>
    );
  };

  const renderDocPreviewModal = () => {
    if (!previewDocUrl) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 5000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }} onClick={() => setPreviewDocUrl(null)}>
        <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%' }}>
          <div onClick={() => setPreviewDocUrl(null)} style={{ position: 'absolute', top: -40, right: 0, cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', padding: 4, display: 'flex' }}><CloseIcon size={24} color="white" /></div>
          <img src={previewDocUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }} onClick={(e) => e.stopPropagation()} />
        </div>
      </div>
    );
  };

  const renderDeleteConfirmModal = () => {
    if (!deleteConfig.isOpen) return null;
    
    let title = '';
    let message = '';

    if (deleteConfig.type === 'activity') {
      title = t.marketingActivity?.deleteActivityTitle || 'Delete Activity';
      message = t.marketingActivity?.deleteActivityConfirm || 'Are you sure you want to permanently delete this activity?';
    } else if (deleteConfig.type === 'participant') {
      title = t.marketingActivity?.deleteParticipantTitle || 'Delete Participant';
      message = t.marketingActivity?.deleteParticipantConfirm || 'Are you sure you want to delete this participant?';
    } else if (deleteConfig.type === 'minute') {
      title = t.marketingActivity?.deleteMinuteTitle || 'Delete Minute';
      message = t.marketingActivity?.deleteMinuteConfirm || 'Are you sure you want to delete this minute?';
    } else if (deleteConfig.type === 'documentation') {
      title = t.marketingActivity?.deleteDocumentationTitle || 'Delete Documentation';
      message = t.marketingActivity?.deleteDocumentationConfirm || 'Are you sure you want to delete this document?';
    }

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <div style={{ backgroundColor: 'white', borderRadius: 16, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
          <text.H4 style={{marginBottom: 12}}>{title}</text.H4>
          <text.T16 style={{marginBottom: 24, color: theme.colors.bodyTextColor}}>{message}</text.T16>
          <div style={{display: 'flex', gap: 12}}>
            <components.Button title={t.common?.cancel || 'Cancel'} style={{backgroundColor: '#F3F4F6', color: '#4B5563', padding: 10 }} onClick={() => setDeleteConfig({ isOpen: false, type: null, idToDelete: null })} />
            <components.Button title={t.common?.delete || 'Delete'} style={{backgroundColor: '#EF4444', padding: 10 }} onClick={executeDelete} />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id='screen' style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      {renderHeader()}
      {renderContent()}
      {renderSignatureModal()}
      {renderSignaturePreviewModal()}
      {renderDocPreviewModal()}
      {renderDeleteConfirmModal()}
    </div>
  );
};